import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// @mui
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
import httpCommon from 'src/http-common';
import { storage } from 'src/utils/storage';
import {
  DEFAULT_CLIENT,
  fetchDefaultSiteCode,
  mapToClientKey,
} from 'src/_core/config/clientSelector';
import { encryptData } from '../jwt/encryptionUtils';

// ----------------------------------------------------------------------

export const base64UrlDecode = (value) => {
  let base64 = String(value).replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const applySsoLogin = async (loginData) => {
  if (!loginData?.site_cd || !loginData?.emp_mst_login_id) {
    throw new Error('SSO payload is missing site_cd or emp_mst_login_id');
  }

  // Microsoft login is only offered for the STANDARD client
  if (mapToClientKey(loginData.site_cd) !== DEFAULT_CLIENT) {
    throw new Error(`Microsoft login is not enabled for site ${loginData.site_cd}`);
  }

  storage.set('loginId', encryptData(loginData.emp_mst_login_id));
  storage.set('siteCode', encryptData(loginData.site_cd));
  storage.set('siteName', encryptData(loginData.site_name || ''));
  storage.set('employeeId', encryptData(loginData.emp_mst_empl_id || ''));
  storage.set('employeeName', encryptData(loginData.emp_mst_name || ''));
  storage.set('department', encryptData(loginData.emp_det_department || ''));
  storage.set('ldapAuthWw', String(loginData.dft_mst_ldap_auth_ww ?? '0'));

  await fetchDefaultSiteCode();
};

const resolveLoginData = async (searchParams) => {
  const ticket = searchParams.get('ticket');
  if (ticket) {
    const response = await httpCommon.post('/sso_exchange.php', { ticket });
    if (response?.data?.status !== 'SUCCESS') {
      throw new Error(response?.data?.message || 'SSO ticket exchange failed');
    }
    return response.data.data;
  }

  const data = searchParams.get('data');
  if (data) {
    return JSON.parse(base64UrlDecode(data));
  }

  throw new Error('SSO callback has no ticket or data');
};

// ----------------------------------------------------------------------

export default function SsoCallbackView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('Signing you in…');
  const handledRef = useRef(false);

  useEffect(() => {
    // Ticket is single-use; guard against StrictMode double-invoke
    if (handledRef.current) return;
    handledRef.current = true;

    (async () => {
      try {
        const loginData = await resolveLoginData(searchParams);
        await applySsoLogin(loginData);
        setStatus('Signed in. Going to dashboard…');
        navigate(PATH_AFTER_LOGIN, { replace: true });
      } catch (error) {
        console.error('SSO callback failed to apply session', error);
        storage.clearLoginData();
        setStatus('SSO sign-in failed. Returning to login…');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    })();
  }, [navigate, searchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center', minWidth: 300 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          Microsoft SSO
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {status}
        </Typography>
      </Paper>
    </Box>
  );
}
